import {
  forwardRef,
  ComponentPropsWithoutRef,
  useId,
  useRef,
  ChangeEvent,
  useState,
  useEffect,
} from "react";
import cx from "classnames";
import { useMergeRefs } from "@floating-ui/react";

import textfieldStyles from "../TextField/TextField.module.scss";
import Close from "@servicetitan/hammer-icon/mdi/round/close.svg";
import Search from "@servicetitan/hammer-icon/mdi/round/search.svg";
import styles from "./SearchField.module.scss";
import { LayoutUtilProps, Size } from "../../types";
import { useLayoutPropsUtil } from "../../hooks";
import { SrOnly } from "../SrOnly";
import { Icon } from "../Icon";

/**
 * Props for the SearchField component
 * @extends Omit<ComponentPropsWithoutRef<"input">, "size" | "prefix" | "required" | "children">
 * @extends LayoutUtilProps
 */
export type SearchFieldProps = Omit<
  ComponentPropsWithoutRef<"input">,
  "size" | "prefix" | "required" | "children"
> &
  LayoutUtilProps & {
    /**
     * Size of the search field
     * @default "medium"
     */
    size?: Extract<Size, "small" | "medium" | "large">;
    /**
     * Whether the search field is disabled
     * @default false
     */
    disabled?: boolean;
    /**
     * Callback when the clear button is clicked
     */
    onClear?: () => void;
  };

/**
 * SearchField component for text input with search functionality.
 *
 * Features:
 * - Search icon prefix for visual clarity
 * - Clear button that appears when input has value
 * - Multiple size variants (small, medium, large)
 * - Controlled and uncontrolled modes
 * - Disabled and read-only states
 * - Accessibility support with proper ARIA attributes
 * - Layout utility props for positioning and spacing
 * - Automatic value detection for clear button visibility
 * - Search-specific input type and autocomplete settings
 * - Screen reader support with placeholder announcements
 *
 * @example
 * <SearchField
 *   placeholder="Search users..."
 *   onChange={(e) => setSearchTerm(e.target.value)}
 *   onClear={() => setSearchTerm("")}
 * />
 *
 * @example
 * <SearchField
 *   size="large"
 *   value={searchValue}
 *   onChange={(e) => setSearchValue(e.target.value)}
 *   disabled={isLoading}
 * />
 */
export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(
  function SearchField(props, ref) {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      className,
      size,
      onChange,
      onClear,
      id: idProp,
      disabled,
      style,
      value,
      placeholder = "Search...",
      autoComplete = "off",
      ...rest
    } = componentProps;

    const [hasValue, setHasValue] = useState(
      rest.defaultValue || value ? true : false,
    );
    const placeholderUid = useId();

    const inputRef = useRef<HTMLInputElement>(null);

    const styleCombined = { ...style, ...layoutStyles };

    const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.value) {
        setHasValue(true);
      } else {
        setHasValue(false);
      }
      onChange?.(e);
    };

    useEffect(() => {
      if (typeof value === "undefined") return;
      if (value === "" || !value) {
        setHasValue(false);
      } else {
        setHasValue(true);
      }
    }, [value]);

    const handleClear = () => {
      if (!inputRef.current) return;
      if (disabled || rest.readOnly) return;
      onClear?.();
      if (value) {
        return;
      }
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        "value",
      )?.set;
      nativeInputValueSetter?.call(inputRef.current, "");
      inputRef.current.dispatchEvent(new Event("input", { bubbles: true }));
      setHasValue(false);
    };

    return (
      <div
        className={cx(
          textfieldStyles["textfield"],
          styles["searchfield"],
          className,
        )}
        data-anv="SearchField"
        style={styleCombined}
      >
        <div
          className={cx(
            textfieldStyles["input-wrapper"],
            styles["input-wrapper"],
            {
              [textfieldStyles["small"]]: size === "small",
              [textfieldStyles["large"]]: size === "large",
            },
          )}
          role="presentation"
          onClick={() => inputRef.current?.focus()}
        >
          <div aria-hidden className={textfieldStyles["prefix"]}>
            <Icon aria-label="search" svg={Search} />
          </div>
          <input
            className={cx(textfieldStyles["input"])}
            ref={useMergeRefs([ref, inputRef])}
            onChange={handleOnChange}
            placeholder={placeholder}
            value={value}
            type="search"
            autoComplete={autoComplete}
            disabled={disabled}
            aria-labelledby={placeholderUid}
            {...rest}
          />
          <Icon
            aria-label="cancel"
            svg={Close}
            role="button"
            onClick={handleClear}
            className={styles["clear"]}
            aria-disabled={disabled}
            style={{
              visibility: hasValue ? "visible" : "hidden",
            }}
          />
        </div>
        <SrOnly id={placeholderUid}>{placeholder}</SrOnly>
      </div>
    );
  },
);
